import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { concat, Observable, of } from 'rxjs';
import { catchError, flatMap, map, switchMap, tap } from 'rxjs/operators';
import { BrowserManagerDataDispatcher } from '../browser-manager-data/browser-manager-data.dispatcher';
import {
  ActionsEnum,
  FetchScreen,
  HideFeedbackCardForFeature,
  SendFeedback,
  SendFeedback200,
  sendFeedbackFailure,
  SendFeedbackFailure,
  sendFeedbackSuccess,
  SendFeedbackSuccess,
  setScreen,
  SetScreen,
} from './feedback-data.actions';
import {
  FEEDBACK_API_ERROR_GROUPING_KEY,
  FeedbackScreenEnum,
  getHideFeedbackCardKey,
} from './feedback-data.data';
import { FeedbackDataService } from './feedback-data.service';
import { IToasterConfig } from '../toaster/toaster.data';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../tracking/tracking-object.builder';
import { DateTimeService, IResponseInfo } from 'i2g-ng-utils';
import { ErrorService } from '../error-reporting/error.service';
import { ToasterDispatcher } from '../toaster/toaster.dispatcher';
import { TrackEvent, trackEvent } from '../tracking/tracking.actions';
import { FetchOffer, fetchOffer } from '../pricing-data/pricing-data.actions';
import { OfferTypesEnum } from '../pricing-data/pricing-data.data';

@Injectable()
export class FeedbackDataEffects {
  constructor(
    private _actions: Actions,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _feedbackDataService: FeedbackDataService,
    private _translateService: TranslateService,
    private _errorService: ErrorService,
    private _localStorageService: LocalStorageService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _dateTimeService: DateTimeService,
    private _toasterDispatcher: ToasterDispatcher
  ) {}

  @Effect()
  fetchScreen: Observable<SetScreen> = this._actions.pipe(
    ofType(ActionsEnum.FETCH_SCREEN),
    switchMap(({ payload }: FetchScreen) => {
      const { screenId } = payload;

      return this._feedbackDataService.fetchScreen(screenId).pipe(
        map(({ body }: IResponseInfo) =>
          setScreen(screenId, body['screen'] || false)
        ),
        catchError(() => of(setScreen(screenId, false)))
      );
    })
  );

  @Effect()
  sendFeedback200: Observable<
    SendFeedbackSuccess | SendFeedbackFailure
  > = this._actions.pipe(
    ofType(ActionsEnum.SEND_FEEDBACK_200),
    flatMap(({ payload }: SendFeedback200) => {
      const toastConfig: IToasterConfig = {
        type: 'feedback-success',
        component: MessageToastComponent,
        data: {
          label: this._translateService.instant(
            'DIALOG.FEEDBACK.RESPONSE_MESSAGE'
          ),
        },
      };

      // always show success
      this._toasterDispatcher.showToast(toastConfig);

      return this._feedbackDataService.sendFeedback200(payload).pipe(
        map(() => sendFeedbackSuccess()),
        catchError((error) => of(sendFeedbackFailure(error)))
      );
    })
  );

  @Effect()
  sendFeedback: Observable<
    SendFeedbackSuccess | FetchOffer | SendFeedbackFailure
  > = this._actions.pipe(
    ofType(ActionsEnum.SEND_FEEDBACK),
    flatMap(({ payload }: SendFeedback) => {
      return this._feedbackDataService.sendFeedback(payload).pipe(
        flatMap(() => {
          const actions = [];

          if (payload.screen === FeedbackScreenEnum.cancel_subscription) {
            actions.push(fetchOffer(OfferTypesEnum.cancellation_winback));
          }

          actions.push(sendFeedbackSuccess());
          return concat(actions);
        }),
        catchError((error) => of(sendFeedbackFailure(error)))
      );
    })
  );

  @Effect()
  sendFeedbackFailure: Observable<TrackEvent> = this._actions.pipe(
    ofType(ActionsEnum.SEND_FEEDBACK_FAILURE),
    flatMap(({ payload }: SendFeedbackFailure) => {
      const { error } = payload;
      let errorCode, errorMessage;

      if (error instanceof Error) {
        this._errorService.alertSystem(error, {
          customGroupingKey: FEEDBACK_API_ERROR_GROUPING_KEY,
        });

        errorMessage = error.message;
      } else {
        errorCode =
          error && error.length >= 2 ? error[1].code : 'Unexpected Error';
        errorMessage =
          error && error.length >= 2
            ? error[1].description
            : 'Unexpected Error';
      }

      const trackingObject = this._trackingObjectBuilder.buildFeedback();
      const trackingAction = this._trackingActionBuilder.buildFailed(
        errorCode,
        errorMessage
      );

      return of(trackEvent(trackingObject, trackingAction));
    })
  );

  @Effect({ dispatch: false })
  hideFeedbackCardForAccount: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.HIDE_FEEDBACK_CARD_FOR_FEATURE),
    tap(({ payload }: HideFeedbackCardForFeature) => {
      const hideFeedbackCardKey = getHideFeedbackCardKey(
        payload.featureName,
        payload.accountId
      );

      const feedbackCardHidden = this._localStorageService.getItem(
        hideFeedbackCardKey
      );
      // if key is already set, do nothing. wait for the timeout
      if (feedbackCardHidden) {
        return;
      }

      // hide feedback banner for 28 days
      const expiryTime = this._dateTimeService.addTime(
        this._dateTimeService.getNow('X'),
        28,
        'days',
        'X',
        false,
        false
      );

      this._browserManagerDataDispatcher.setLocalStorageItem(
        hideFeedbackCardKey,
        expiryTime
      );
    })
  );
}
